import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { UsersFieldsService } from './users.fields.service';
import {
	AlarisComplexTableComponent,
	AlarisDialogService, AlarisFilterManagerComponent,
	AlarisTableStateService,
	EditPanelWidth,
	RowActionSimple,
	TABLE_STATE_SERVICE_INJECTOR
} from '@campaign-portal/components-library';
import { User } from '@helpers/types/models';
import { TableSettingsService } from '@helpers/api/table-settings.service';
import { TableTypes } from '@campaign-portal/namespace/entities/table-settings/specs';
import { FilterType, Paging } from '@campaign-portal/namespace/common/rpc.params';
import { UsersService } from './users.service';
import { UsersDeleteDialogComponent } from './dialog/dialog.component';
import { BehaviorSubject } from 'rxjs';
import { EditUserComponent } from './edit-user/edit-user.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../../auth/user.service';
import { RolesService } from '../roles/roles.service';

@Component({
	selector: 'app-users',
	templateUrl: './users.component.html',
	styleUrl: '../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss',
	providers: [
		AlarisTableStateService,
		{ provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService }
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class UsersComponent extends AlarisComplexTableComponent<User> implements OnDestroy {

	override readonly rowActions: RowActionSimple<any>[] = [
		{
			icon: 'icon-edit',
			label: 'actions.edit',
			action: this.edit.bind(this),
			disabled: (): boolean => {
				return !this.user.allowed(['users.edit'])
			}
		},
		{
			icon: 'icon-delete',
			label: 'actions.delete',
			action: this.delete.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => {
				return !this.user.allowed(['users.edit'])
			}
		}
	];
	override readonly dblClickRowAction = this.edit;
	override readonly pageSizeOptions = [25, 50, 100, 200];
	override readonly paging: Paging = {
		Skip: this.activePage,
		Take: this.pageSize
	};

	readonly exporting$ = new BehaviorSubject<boolean>(false);

	constructor(
		dataService: UsersService,
		public override readonly fieldsService: UsersFieldsService,
		tableSettingsService: TableSettingsService,
		private readonly dialog: AlarisDialogService,
		private readonly usersService: UsersService,
		protected readonly user: UserService,
		private readonly roles: RolesService
	) {
		super(
			dataService,
			fieldsService,
			tableSettingsService,
			TableTypes.INTERNAL
		);
		this.state.filters()
			.set(this.fieldsService.name.variable, { enabled: true, filterType: FilterType.LIKE })
			.set(this.fieldsService.login.variable, { enabled: true, filterType: FilterType.LIKE })
			.set(this.fieldsService.email.variable, { enabled: true, filterType: FilterType.LIKE })
		this.roles.load().pipe(takeUntilDestroyed()).subscribe();
	}

	override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.exporting$.next(false);
		this.exporting$.complete();
	}

	edit(user?: User): void {
		this.editPanel.open<EditUserComponent, { user: User }, boolean>(EditUserComponent, EditPanelWidth.SM, { user })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if ( result ) {
					this.load();
				}
			});
	}

	delete(user: User): void {
		this.dialog.open<boolean>(UsersDeleteDialogComponent, { autoFocus: false, data: { user } })
			.closed
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if ( result ) {
					this.load();
				}
			});
	}

	export(): void {
		this.exporting$.next(true);
		this.usersService.export(this.total)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.exporting$.next(false);
			});
	}

	override editTableFilter(): void {
		this.editPanel.open(AlarisFilterManagerComponent, EditPanelWidth.MD, {
			filtersChange: this.filtersChange,
			filters: this.state.filters,
			tHeaders: this.tHeaders()
		});
	}

}
